import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, } from 'react-router-dom';

import { Box, H6, Paper, CircularProgress, Skeleton, RichTextEditor, Dayjs, dayjs } from '@parspec/pixel';

import { BomTable } from './BomTable/BomTable';
import { Header } from './Header';
import { BomModal } from '../Dashboard/BOMModal';
import {
    useEditProjectDetails,
    useGetProjectDetailsQuery,
    useGetProjectFoldersAndFiles,
    useGetProjectStakeholdersQuery,
    useUpdateStakeholderInfoMutation
} from './queries';
import EditProjectDetail from '../shared/CreateAndEditProjectModal/EditProjectDetail';
import { IProjectDetailState, Errors } from '../shared/utils/utils';
import { useSubscriptionInfo } from '../Settings/PlanAndPayment/queries';
import { convertToCents } from '../shared/utils/utils';
import { projectStakeHoldersInitialState, StakeholderType } from '../shared/CreateAndEditProjectModal/utils';
import ProjectsTable from '../shared/ProjectsTable/ProjectFilesTable';


const ProjectDetails = () => {
    const { projectId } = useParams();
    const [stakeholdersInfo, setStakeholdersInfo] = useState<StakeholderType[]>(projectStakeHoldersInitialState);

    const projectStateRef = useRef<IProjectDetailState>();
    const setProjectStateRef = useCallback((data: IProjectDetailState) => {
        projectStateRef.current = data;
    }, []);

    const [formErrors, setFormErrors] = useState<Errors>({ projectNameError: '' });
    const [bomModalOpen, setBomModalOpen] = useState<boolean>(false);
    const { data: projectStakeholders, isFetching } = useGetProjectStakeholdersQuery(Number(projectId || 0));
    const { isFetching: isSubscriptionFetching } = useSubscriptionInfo();
    const { data: projectDetails, isLoading: isEditProjectLoading } = useGetProjectDetailsQuery(Number(projectId));
    const {
        data: projectFoldersAndFiles,
        isLoading: isLoadingProjectFoldersAndFiles,
    } = useGetProjectFoldersAndFiles(Number(projectId));

    const { mutateAsync: editProjectDetails, isLoading: isUpdatingProjectDetails } = useEditProjectDetails(true);
    const { mutateAsync: updateStakeholder } = useUpdateStakeholderInfoMutation();

    useEffect(() => {
        if (projectStakeholders?.data && !isFetching) {
            setStakeholdersInfo(projectStakeholders?.data);
        }
    }, [projectStakeholders?.data, isFetching]);

    const handleFormErrorsAction = useCallback((error: Errors) => {
        setFormErrors(error);
    }, []);

    const handleBOMModalDialog = () => {
        setBomModalOpen(!bomModalOpen);
    };

    async function handleProjectName(value: string) {
        if (projectStateRef?.current) {
            projectStateRef.current.name = value;
            await handleOnBlur();
        }
    }

    const handleUpdateProjectStage = async (currentProjectStage: string, bidDueDate?: Dayjs | null) => {
        if (projectStateRef?.current) {
            projectStateRef.current.stage = currentProjectStage;
            projectStateRef.current.quote_due_date = dayjs(bidDueDate).isValid() ? dayjs(bidDueDate) : projectStateRef.current.quote_due_date;
            await handleOnBlur();
        }
    }

    async function handleOnBlur() {
        if (projectStateRef?.current) {
            if (
                (projectStateRef.current.submittal_due_date !== null &&
                    projectStateRef.current.submittal_due_date &&
                    !projectStateRef.current.submittal_due_date.isValid() &&
                    projectStateRef.current.submittal_due_date.year() > 2099) ||
                (projectStateRef.current.o_m_due_date !== null &&
                    projectStateRef.current.o_m_due_date &&
                    !projectStateRef.current.o_m_due_date.isValid() &&
                    projectStateRef.current.o_m_due_date.year() > 2099)
            ) {
                return;
            }

            const formData = new FormData();
            formData.append('name', projectStateRef.current.name);
            formData.append('company', JSON.stringify(projectStateRef.current.company));
            formData.append('stage', projectStateRef.current.stage);
            formData.append(
                'estimated_project_cost',
                projectStateRef.current.estimated_project_cost === null ? 'null' : JSON.stringify(convertToCents(projectStateRef.current.estimated_project_cost))
            );
            formData.append('quote_owner', JSON.stringify(projectStateRef.current.quote_owner));
            formData.append('quote_due_date', projectStateRef.current.quote_due_date ? projectStateRef.current.quote_due_date.format() : 'null');
            formData.append('submittal_owner', JSON.stringify(projectStateRef.current.submittal_owner));
            formData.append('submittal_due_date', projectStateRef.current.submittal_due_date ? projectStateRef.current.submittal_due_date.format() : 'null');
            formData.append('o_m_owner', JSON.stringify(projectStateRef.current.o_m_owner));
            formData.append('o_m_due_date', projectStateRef.current.o_m_due_date ? projectStateRef.current.o_m_due_date.format() : 'null');
            formData.append('address1', projectStateRef.current.address1);
            formData.append('address2', projectStateRef.current.address2);
            formData.append('country', projectStateRef.current.country);
            formData.append('city', projectStateRef.current.city);
            formData.append('state', projectStateRef.current.state);
            formData.append('zip', projectStateRef.current.zip);
            formData.append('building_type', projectStateRef.current.building_type);
            formData.append('construction_scope', projectStateRef.current.construction_scope);
            formData.append('priority', projectStateRef.current.priority);
            formData.append('erp_id', projectStateRef.current.erp_id || '');
            if (projectStateRef.current.size) {
                formData.append('size', projectStateRef.current.size);
            } else {
                formData.append('size', '');
            }
            formData.append('internal_notes', projectStateRef.current?.internal_notes ? projectStateRef.current.internal_notes : '');
            // @ts-ignore:next-line
            if (!projectStateRef.current.logo?.filepath) {
                // @ts-ignore:next-line
                formData.append('logo', projectStateRef.current.logo);
            }

            if (projectId) {
                await editProjectDetails({ project_id: projectId!, data: formData });
            }
        }
    }

    const updateStakeholderInfo = async (info: StakeholderType, index: number) => {
        setStakeholdersInfo((old) => {
            const updatedInfo = [...old];
            updatedInfo[index] = info;
            return updatedInfo;
        });
        if (info?.id) {
            const input = {
                id: info?.id,
                projectId: Number(projectId || 0),
                company: info?.company?.id || null,
                contact: info?.contact?.id || null
            };
            await updateStakeholder(input);
        }
    };

    function onEditorBlur(html: string) {
        if (projectStateRef?.current) {
            projectStateRef.current.internal_notes = html;
            handleOnBlur();
        }
    }

    return (
        <>
            {bomModalOpen && (
                <BomModal
                    open={bomModalOpen}
                    onCloseClick={() => {
                        setBomModalOpen(false);
                    }}
                    onSuccess={handleBOMModalDialog}
                    selectedProjectId={Number(projectId)}
                />
            )}
            <Box>
                <Header getProjectName={handleProjectName} formErrors={formErrors} onError={handleFormErrorsAction} />
                {isSubscriptionFetching ? (
                    <Box display="flex" alignItems="center" justifyContent="center" height={'100vh'}>
                        <CircularProgress color="primary" size="xxl" />
                    </Box>
                ) : (
                    <Box py={4} px={6} overflow="auto" height="calc(100vh - 140px)" bgcolor="neutral.light">
                        <H6 mb={3}>Project Details</H6>
                        <Paper variant="outlined">
                            <Box py={6}>
                                <EditProjectDetail
                                    editMode={true}
                                    onError={handleFormErrorsAction}
                                    formErrors={formErrors}
                                    setProjectStateRef={setProjectStateRef}
                                    onBlurAction={handleOnBlur}
                                    isUpdatingProjectDetails={isUpdatingProjectDetails}
                                    stakeholdersInfo={stakeholdersInfo}
                                    updateStakeholderInfo={updateStakeholderInfo}
                                    isStakeHoldersInfoFetching={isFetching}
                                />
                            </Box>
                        </Paper>

                        <Box mt={4} display="flex" gap="16px">
                            <Box display="flex" flexDirection="column" gap="8px" flex={1}>
                                <H6 flexShrink={0}>Project Notes & Files (Internal)</H6>
                                {isEditProjectLoading ?
                                    <Skeleton variant="rectangular" height={232} width="100%" />
                                    : (
                                        <Box flex={1} display="flex" flexDirection="column">
                                            <RichTextEditor
                                                initialHtml={projectDetails?.data.internal_notes || ''}
                                                placeHolderText="Add important notes, or links to files relevant to this project."
                                                onBlur={onEditorBlur}
                                                contentEditableHeight="190px"
                                            />
                                        </Box>
                                    )}
                            </Box>
                            <Box display="flex" flex={1}>
                                {isEditProjectLoading ?
                                    <Skeleton variant="rectangular" height={276} width="100%" />
                                    : (
                                        <ProjectsTable
                                            isLoading={isLoadingProjectFoldersAndFiles}
                                            nestedFolders={projectFoldersAndFiles?.data || []}
                                            tableHeight="100%"
                                            isActionButtonsVisible
                                        />
                                    )}
                            </Box>
                        </Box>
                        <BomTable setModalOpen={setBomModalOpen} onUpdateProjectStage={handleUpdateProjectStage} isUpdatingProjectDetails={isUpdatingProjectDetails} />
                    </Box>
                )}
            </Box>
        </>
    );
};

export default ProjectDetails;
